/**
 * Subcontractors directory — Phase 1.
 *
 * Lists every sub the org works with. Add / edit inline; tap a
 * row to drill in. Future Phase 2 will add per-job assignments
 * + a calendar of who's on which site this week.
 */

import {
  AlertCircle,
  CheckCircle2,
  HardHat,
  Mail,
  MapPin,
  Pencil,
  Phone,
  Plus,
  ShieldCheck,
  Trash2,
  X,
} from "lucide-react";
import { type FormEvent, useState } from "react";

import { ApiError } from "@/api/client";
import {
  useCreateSubcontractor,
  useCurrentUser,
  useDeleteSubcontractor,
  useOrganizations,
  useSubcontractors,
  useUpdateSubcontractor,
} from "@/api/hooks";
import type { CreateSubcontractorRequest, SubcontractorResponse } from "@/api/types";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { HelpPopover } from "@/components/HelpPopover";

export function SubcontractorsPage() {
  const me = useCurrentUser();
  const isPencilOutAdmin = me.data?.role === "pencilout_admin";
  const [orgFilter, setOrgFilter] = useState<string | null>(null);
  const [tradeFilter, setTradeFilter] = useState<string>("");
  const orgsQuery = useOrganizations();
  const subsQuery = useSubcontractors(
    isPencilOutAdmin ? orgFilter : null,
    tradeFilter.trim() || null,
  );
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<SubcontractorResponse | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState<SubcontractorResponse | null>(null);
  const deleteSub = useDeleteSubcontractor();

  // Distinct trades from the loaded list, for the trade filter
  // pill row. Sorted + de-duped client-side.
  const trades = Array.from(
    new Set((subsQuery.data ?? []).map((s) => s.trade ?? "").filter(Boolean)),
  ).sort();

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <div className="flex items-center gap-1.5">
            <h1 className="font-mono text-2xl font-bold tracking-tight">Subcontractors</h1>
            <HelpPopover title="Subcontractor directory">
              <p>
                Track your trade subs (electrical, plumbing, drywall, etc.) with their license +
                insurance details in one place.
              </p>
              <p>
                COI (Certificate of Insurance) expiration dates show as color-coded badges so you
                don't accidentally use a sub with lapsed coverage. Red = expired, yellow = expiring
                within 30 days.
              </p>
              <p>
                Phase 2 will let you assign subs to specific jobs + track their schedule and
                payouts.
              </p>
            </HelpPopover>
          </div>
          <p className="mt-1 text-sm text-graphite-soft">
            Your network of trade subs. Add new ones inline; tap a row to edit. Job-level
            assignments + scheduling come in a follow-up.
          </p>
        </div>
        {!creating && (
          <button
            type="button"
            onClick={() => setCreating(true)}
            className="inline-flex items-center gap-1.5 rounded bg-graphite px-3 py-2 font-mono text-sm font-semibold text-paper transition hover:bg-graphite-soft"
          >
            <Plus className="h-3.5 w-3.5" aria-hidden />
            New sub
          </button>
        )}
      </div>

      {creating && (
        <SubcontractorForm
          mode="create"
          isPencilOutAdmin={isPencilOutAdmin}
          orgs={orgsQuery.data ?? []}
          onClose={() => setCreating(false)}
        />
      )}

      {editing && (
        <SubcontractorForm
          mode="edit"
          sub={editing}
          isPencilOutAdmin={isPencilOutAdmin}
          orgs={orgsQuery.data ?? []}
          onClose={() => setEditing(null)}
        />
      )}

      {/* Trade pill filter — only when there's more than one
          distinct trade in the loaded list. Clicking a pill
          re-filters server-side. */}
      {trades.length > 1 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-[11px] uppercase tracking-wider text-graphite-soft">
            Trade
          </span>
          <button
            type="button"
            onClick={() => setTradeFilter("")}
            className={
              tradeFilter === ""
                ? "rounded-full bg-graphite px-2.5 py-0.5 font-mono text-xs font-semibold text-paper"
                : "rounded-full border border-rule bg-paper px-2.5 py-0.5 font-mono text-xs text-graphite-soft hover:text-graphite"
            }
          >
            all
          </button>
          {trades.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTradeFilter(t)}
              className={
                tradeFilter === t
                  ? "rounded-full bg-graphite px-2.5 py-0.5 font-mono text-xs font-semibold text-paper"
                  : "rounded-full border border-rule bg-paper px-2.5 py-0.5 font-mono text-xs text-graphite-soft hover:text-graphite"
              }
            >
              {t}
            </button>
          ))}
        </div>
      )}

      {isPencilOutAdmin && (
        <div className="flex items-center gap-2">
          <span className="font-mono text-[11px] uppercase tracking-wider text-graphite-soft">
            Org
          </span>
          <select
            value={orgFilter ?? ""}
            onChange={(e) => setOrgFilter(e.target.value || null)}
            className="rounded border border-rule bg-paper-warm px-2 py-1 font-mono text-xs text-graphite focus:border-graphite focus:outline-none"
          >
            <option value="">All orgs</option>
            {(orgsQuery.data ?? []).map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {subsQuery.isLoading && !subsQuery.data && (
        <p className="text-sm text-graphite-soft">Loading subs…</p>
      )}

      {subsQuery.error && !subsQuery.data && (
        <p className="text-sm text-red-pencil">
          {subsQuery.error instanceof ApiError
            ? subsQuery.error.detail
            : "Could not load subcontractors."}
        </p>
      )}

      {subsQuery.data && subsQuery.data.length === 0 && (
        <div className="rounded-lg border border-rule bg-paper-warm p-12 text-center">
          <HardHat className="mx-auto h-8 w-8 text-graphite-faint" aria-hidden />
          <p className="mt-4 text-graphite-soft">
            No subs yet. Tap "New sub" to add the first one.
          </p>
        </div>
      )}

      {subsQuery.data && subsQuery.data.length > 0 && (
        <ul className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
          {subsQuery.data.map((sub) => (
            <SubcontractorCard
              key={sub.id}
              sub={sub}
              onEdit={() => setEditing(sub)}
              onDelete={() => setConfirmingDelete(sub)}
            />
          ))}
        </ul>
      )}

      {confirmingDelete && (
        <ConfirmDialog
          title={`Delete ${confirmingDelete.name}?`}
          body="Removes them from your sub directory. Doesn't affect any jobs they were on (we don't link them yet)."
          confirmLabel={deleteSub.isPending ? "Deleting…" : "Delete"}
          variant="danger"
          busy={deleteSub.isPending}
          onConfirm={() => {
            deleteSub.mutate(confirmingDelete.id, {
              onSettled: () => setConfirmingDelete(null),
            });
          }}
          onCancel={() => setConfirmingDelete(null)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------

function SubcontractorCard({
  sub,
  onEdit,
  onDelete,
}: {
  sub: SubcontractorResponse;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const insuranceState = insuranceStateFor(sub.insurance_expires_at);

  return (
    <li className="flex flex-col gap-2 rounded-lg border border-rule bg-paper-warm p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <h3 className="font-semibold leading-tight">{sub.name}</h3>
          {sub.trade && (
            <p className="mt-0.5 font-mono text-[11px] uppercase tracking-wider text-graphite-soft">
              {sub.trade}
            </p>
          )}
        </div>
        <div className="flex flex-none gap-1">
          <button
            type="button"
            onClick={onEdit}
            className="rounded p-1 text-graphite-soft hover:bg-pencil-yellow-soft hover:text-graphite"
            aria-label="Edit"
          >
            <Pencil className="h-3.5 w-3.5" aria-hidden />
          </button>
          <button
            type="button"
            onClick={onDelete}
            className="rounded p-1 text-graphite-soft hover:bg-red-pencil/10 hover:text-red-pencil"
            aria-label="Delete"
          >
            <Trash2 className="h-3.5 w-3.5" aria-hidden />
          </button>
        </div>
      </div>

      {(sub.phone || sub.email) && (
        <div className="space-y-0.5 text-sm">
          {sub.phone && (
            <a
              href={`tel:${sub.phone}`}
              className="flex items-center gap-1.5 text-graphite hover:text-ink hover:underline"
            >
              <Phone className="h-3 w-3 flex-none text-graphite-soft" aria-hidden />
              {sub.phone}
            </a>
          )}
          {sub.email && (
            <a
              href={`mailto:${sub.email}`}
              className="flex items-center gap-1.5 text-graphite hover:text-ink hover:underline"
            >
              <Mail className="h-3 w-3 flex-none text-graphite-soft" aria-hidden />
              {sub.email}
            </a>
          )}
        </div>
      )}

      {(sub.city || sub.region) && (
        <div className="flex items-center gap-1.5 text-sm text-graphite-soft">
          <MapPin className="h-3 w-3 flex-none" aria-hidden />
          {[sub.city, sub.region].filter(Boolean).join(", ")}
        </div>
      )}

      {insuranceState !== "none" && (
        <div
          className={`flex items-center gap-1.5 rounded px-2 py-1 font-mono text-[11px] ${
            insuranceState === "expired"
              ? "bg-red-pencil/10 text-red-pencil"
              : insuranceState === "expiring_soon"
                ? "bg-burnt/10 text-burnt"
                : "bg-olive/10 text-olive"
          }`}
        >
          {insuranceState === "current" ? (
            <ShieldCheck className="h-3 w-3" aria-hidden />
          ) : (
            <AlertCircle className="h-3 w-3" aria-hidden />
          )}
          {insuranceCopy(insuranceState, sub.insurance_expires_at)}
        </div>
      )}
    </li>
  );
}

type InsuranceState = "none" | "current" | "expiring_soon" | "expired";

function insuranceStateFor(expiresAt: string | null): InsuranceState {
  if (!expiresAt) return "none";
  const exp = new Date(expiresAt).getTime();
  if (Number.isNaN(exp)) return "none";
  const now = Date.now();
  if (exp < now) return "expired";
  if (exp < now + 30 * 86_400_000) return "expiring_soon";
  return "current";
}

function insuranceCopy(state: InsuranceState, iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso).toLocaleDateString();
  switch (state) {
    case "current":
      return `Insured · expires ${d}`;
    case "expiring_soon":
      return `Insurance expires ${d}`;
    case "expired":
      return `Insurance EXPIRED ${d}`;
    default:
      return "";
  }
}

// ---------------------------------------------------------------
// SubcontractorForm — create + edit, mirrors ClientForm shape.
// ---------------------------------------------------------------

function SubcontractorForm({
  mode,
  sub,
  isPencilOutAdmin,
  orgs,
  onClose,
}: {
  mode: "create" | "edit";
  sub?: SubcontractorResponse;
  isPencilOutAdmin: boolean;
  orgs: { id: string; name: string }[];
  onClose: () => void;
}) {
  const create = useCreateSubcontractor();
  const update = useUpdateSubcontractor(sub?.id ?? "");

  const [name, setName] = useState(sub?.name ?? "");
  const [trade, setTrade] = useState(sub?.trade ?? "");
  const [email, setEmail] = useState(sub?.email ?? "");
  const [phone, setPhone] = useState(sub?.phone ?? "");
  const [addr1, setAddr1] = useState(sub?.address_line1 ?? "");
  const [addr2, setAddr2] = useState(sub?.address_line2 ?? "");
  const [city, setCity] = useState(sub?.city ?? "");
  const [region, setRegion] = useState(sub?.region ?? "");
  const [postal, setPostal] = useState(sub?.postal_code ?? "");
  const [country, setCountry] = useState(sub?.country ?? "US");
  const [licenseNumber, setLicenseNumber] = useState(sub?.license_number ?? "");
  const [insuranceExpiresAt, setInsuranceExpiresAt] = useState(
    sub?.insurance_expires_at ? sub.insurance_expires_at.slice(0, 10) : "",
  );
  const [notes, setNotes] = useState(sub?.notes ?? "");
  const [orgId, setOrgId] = useState(sub?.organization_id ?? orgs[0]?.id ?? "");
  const [error, setError] = useState<string | null>(null);

  const submitting = create.isPending || update.isPending;

  function trimOrNull(s: string): string | null {
    const t = s.trim();
    return t.length === 0 ? null : t;
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!name.trim()) {
      setError("Name is required.");
      return;
    }
    const trimmedName = name.trim();
    const body = {
      name: trimmedName,
      trade: trimOrNull(trade),
      email: trimOrNull(email),
      phone: trimOrNull(phone),
      address_line1: trimOrNull(addr1),
      address_line2: trimOrNull(addr2),
      city: trimOrNull(city),
      region: trimOrNull(region),
      postal_code: trimOrNull(postal),
      country: trimOrNull(country),
      license_number: trimOrNull(licenseNumber),
      insurance_expires_at: insuranceExpiresAt
        ? new Date(`${insuranceExpiresAt}T00:00:00Z`).toISOString()
        : null,
      notes: trimOrNull(notes),
    } satisfies Partial<CreateSubcontractorRequest>;

    if (mode === "create") {
      const createBody: CreateSubcontractorRequest = {
        ...body,
        name: trimmedName,
        organization_id: isPencilOutAdmin ? orgId : undefined,
      };
      create.mutate(createBody, {
        onSuccess: () => onClose(),
        onError: (err) => setError(err instanceof ApiError ? err.detail : String(err)),
      });
    } else {
      update.mutate(body, {
        onSuccess: () => onClose(),
        onError: (err) => setError(err instanceof ApiError ? err.detail : String(err)),
      });
    }
  }

  return (
    <form onSubmit={handleSubmit} className="rounded-lg border border-rule bg-paper-warm p-4">
      <div className="flex items-start justify-between gap-2">
        <h2 className="font-semibold">
          {mode === "create" ? "New subcontractor" : `Edit ${sub?.name}`}
        </h2>
        <button
          type="button"
          onClick={onClose}
          className="text-graphite-soft hover:text-graphite"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
        <Field label="Name *">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            className="w-full rounded border border-rule bg-paper px-2 py-1.5 text-sm focus:border-graphite focus:outline-none"
          />
        </Field>
        <Field label="Trade">
          <input
            type="text"
            value={trade}
            onChange={(e) => setTrade(e.target.value)}
            placeholder="electrician, plumber, framing, …"
            className="w-full rounded border border-rule bg-paper px-2 py-1.5 text-sm focus:border-graphite focus:outline-none"
          />
        </Field>
        <Field label="Phone">
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className="w-full rounded border border-rule bg-paper px-2 py-1.5 text-sm focus:border-graphite focus:outline-none"
          />
        </Field>
        <Field label="Email">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full rounded border border-rule bg-paper px-2 py-1.5 text-sm focus:border-graphite focus:outline-none"
          />
        </Field>

        <Field label="Address line 1" className="md:col-span-2">
          <input
            type="text"
            value={addr1}
            onChange={(e) => setAddr1(e.target.value)}
            className="w-full rounded border border-rule bg-paper px-2 py-1.5 text-sm focus:border-graphite focus:outline-none"
          />
        </Field>
        <Field label="Address line 2" className="md:col-span-2">
          <input
            type="text"
            value={addr2}
            onChange={(e) => setAddr2(e.target.value)}
            className="w-full rounded border border-rule bg-paper px-2 py-1.5 text-sm focus:border-graphite focus:outline-none"
          />
        </Field>
        <Field label="City">
          <input
            type="text"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            className="w-full rounded border border-rule bg-paper px-2 py-1.5 text-sm focus:border-graphite focus:outline-none"
          />
        </Field>
        <Field label="State / region">
          <input
            type="text"
            value={region}
            onChange={(e) => setRegion(e.target.value)}
            className="w-full rounded border border-rule bg-paper px-2 py-1.5 text-sm focus:border-graphite focus:outline-none"
          />
        </Field>
        <Field label="ZIP / postal">
          <input
            type="text"
            value={postal}
            onChange={(e) => setPostal(e.target.value)}
            className="w-full rounded border border-rule bg-paper px-2 py-1.5 text-sm focus:border-graphite focus:outline-none"
          />
        </Field>
        <Field label="Country (2-char)">
          <input
            type="text"
            value={country}
            onChange={(e) => setCountry(e.target.value.toUpperCase())}
            maxLength={2}
            className="w-full rounded border border-rule bg-paper px-2 py-1.5 text-sm uppercase focus:border-graphite focus:outline-none"
          />
        </Field>

        <Field label="License number">
          <input
            type="text"
            value={licenseNumber}
            onChange={(e) => setLicenseNumber(e.target.value)}
            className="w-full rounded border border-rule bg-paper px-2 py-1.5 text-sm focus:border-graphite focus:outline-none"
          />
        </Field>
        <Field label="Insurance expires (date)">
          <input
            type="date"
            value={insuranceExpiresAt}
            onChange={(e) => setInsuranceExpiresAt(e.target.value)}
            className="w-full rounded border border-rule bg-paper px-2 py-1.5 text-sm focus:border-graphite focus:outline-none"
          />
        </Field>

        {isPencilOutAdmin && mode === "create" && (
          <Field label="Organization *" className="md:col-span-2">
            <select
              value={orgId}
              onChange={(e) => setOrgId(e.target.value)}
              required
              className="w-full rounded border border-rule bg-paper px-2 py-1.5 text-sm focus:border-graphite focus:outline-none"
            >
              {orgs.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </select>
          </Field>
        )}

        <Field label="Notes (internal)" className="md:col-span-2">
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            className="w-full rounded border border-rule bg-paper px-2 py-1.5 text-sm focus:border-graphite focus:outline-none"
          />
        </Field>
      </div>

      {error && (
        <p className="mt-3 rounded border border-red-pencil/30 bg-red-pencil/5 px-3 py-2 text-sm text-red-pencil">
          {error}
        </p>
      )}

      <div className="mt-4 flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          className="rounded px-3 py-1.5 font-mono text-sm text-graphite-soft hover:text-graphite"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={submitting}
          className="inline-flex items-center gap-1.5 rounded bg-graphite px-3 py-1.5 font-mono text-sm font-semibold text-paper transition hover:bg-graphite-soft disabled:opacity-50"
        >
          <CheckCircle2 className="h-3.5 w-3.5" aria-hidden />
          {submitting ? "Saving…" : mode === "create" ? "Add sub" : "Save"}
        </button>
      </div>
    </form>
  );
}

function Field({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    // biome-ignore lint/a11y/noLabelWithoutControl: caller passes the input as `children`; biome can't see through to confirm it, but the input renders as a descendant of <label> at runtime.
    <label className={`block ${className ?? ""}`}>
      <span className="label-mono mb-1 block">{label}</span>
      {children}
    </label>
  );
}
